import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { DnsZoneSurfaceAttachmentDto } from '@terra/shared-contracts-external-network';
import { externalNetworkInventoryApi } from '../../clients/tester-client/external-network-inventory-api';
import { EXTERNAL_NETWORK_SCOPE_DOMAINS_KEY } from './useExternalNetworkScope';

export const EXTERNAL_NETWORK_DNS_ZONE_ATTACHMENTS_KEY =
  'external-network-dns-zone-attachments';

const invalidateDnsZoneQueries = ({
  queryClient,
  surfaceId,
}: {
  queryClient: QueryClient;
  surfaceId: string;
}) => {
  void queryClient.invalidateQueries({
    queryKey: [EXTERNAL_NETWORK_DNS_ZONE_ATTACHMENTS_KEY, surfaceId],
  });
  void queryClient.invalidateQueries({
    queryKey: [EXTERNAL_NETWORK_SCOPE_DOMAINS_KEY, surfaceId],
  });
};

export function useExternalNetworkDnsZoneAttachments({
  surfaceId,
  enabled = true,
}: {
  surfaceId: string;
  enabled?: boolean;
}) {
  const result = useQuery({
    queryKey: [EXTERNAL_NETWORK_DNS_ZONE_ATTACHMENTS_KEY, surfaceId],
    queryFn: async () => {
      const response =
        await externalNetworkInventoryApi.listDnsZoneAttachments(surfaceId);

      return response.attachments;
    },
    enabled,
  });

  return {
    attachments: result.data ?? [],
    isLoading: result.isLoading,
    isError: result.isError,
  };
}

export function useAttachExternalNetworkDnsZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.attachDnsZone,
    onSuccess: (_data, { surfaceId }) => {
      invalidateDnsZoneQueries({ queryClient, surfaceId });
    },
  });
}

export function useDetachExternalNetworkDnsZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.detachDnsZone,
    onSuccess: (_data, { surfaceId }) => {
      invalidateDnsZoneQueries({ queryClient, surfaceId });
    },
  });
}

export function useSyncExternalNetworkDnsZones() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.syncDnsZones,
    onSuccess: (_data, { surfaceId }) => {
      invalidateDnsZoneQueries({ queryClient, surfaceId });
    },
  });
}

export function useVerifyExternalNetworkDnsZoneDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.verifyDnsZoneDelegation,
    onSuccess: (_data, { surfaceId }) => {
      invalidateDnsZoneQueries({ queryClient, surfaceId });
    },
  });
}

export function useUpdateExternalNetworkDnsZonePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: externalNetworkInventoryApi.updateDnsZonePolicy,
    onMutate: async ({ surfaceId, zoneId, body }) => {
      const queryKey = [EXTERNAL_NETWORK_DNS_ZONE_ATTACHMENTS_KEY, surfaceId];

      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<DnsZoneSurfaceAttachmentDto[]>(queryKey);

      queryClient.setQueryData<DnsZoneSurfaceAttachmentDto[]>(
        queryKey,
        (current) =>
          current?.map((attachment) =>
            attachment.zoneId === zoneId
              ? {
                  ...attachment,
                  enabled: body.enabled ?? attachment.enabled,
                  autoScope: body.autoScope ?? attachment.autoScope,
                  recordExclusionPatterns:
                    body.recordExclusionPatterns ??
                    attachment.recordExclusionPatterns,
                }
              : attachment,
          ),
      );

      return { previous, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (!context?.previous) {
        return;
      }

      queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _error, { surfaceId }) => {
      invalidateDnsZoneQueries({ queryClient, surfaceId });
    },
  });
}
